import { IonIcon } from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { helpCircleOutline } from "ionicons/icons";
import { z } from "zod";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { TRAVEL_EXPENSE_TYPE } from "@/lib/constants";
import { carIcon } from "@/lib/icons/car.icon";
import { crossIcon } from "@/lib/icons/cross.icon";
import { dotsHorizontalIcon } from "@/lib/icons/dots-horizontal.icon";
import { fileIcon } from "@/lib/icons/file.icon";
import { trainIcon } from "@/lib/icons/train.icon";
import { cn } from "@/lib/style/cva.config";

interface TravelExpensesFormModalProps {
  modalRef: React.RefObject<HTMLIonModalElement>;
  travelExpensesInfoModalRef: React.RefObject<HTMLIonModalElement>;
  formTravelExpenses: any;
  FormElementExpenses: any;
  closeModal: () => void;
}

export function TravelExpensesFormModal({
  modalRef,
  travelExpensesInfoModalRef,
  formTravelExpenses,
  FormElementExpenses,
  closeModal,
}: TravelExpensesFormModalProps) {
  const { t } = useTranslation();
  // FILE INPUT FIX: Use ref-based approach instead of dynamic creation
  // This prevents memory leaks and file handle issues on mobile
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <ModalSheetAutoHeight ref={modalRef} onDidDismiss={closeModal}>
      <FormElementExpenses>
        <div className="flex flex-col gap-6 p-6">
          {/* Header with info icon */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-txt-primary">Reiskosten</h2>
            <button
              type="button"
              onClick={() => travelExpensesInfoModalRef.current?.present()}
              className="flex size-10 items-center justify-center"
            >
              <IonIcon icon={helpCircleOutline} className="text-2xl text-txt-tertiary" />
            </button>
          </div>

          {/* Type input - required */}
          <formTravelExpenses.AppField
            name="types"
            validators={{
              onChange: z
                .array(z.string())
                .min(1, t("actions:declare-travel-expenses.select-transport")),
              onBlur: z
                .array(z.string())
                .min(1, t("actions:declare-travel-expenses.select-transport")),
              onSubmit: z
                .array(z.string())
                .min(1, t("actions:declare-travel-expenses.select-transport")),
            }}
            children={(field: any) => (
              <div className="flex flex-col gap-2">
                <label className="text-base font-medium text-txt-primary">
                  {t("fields:travelExpenses.type.label")}
                </label>
                <div className="flex gap-3 *:flex-1">
                  {TRAVEL_EXPENSE_TYPE.map((type) => {
                    const isActive = field.state.value?.includes(type);
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => field.handleChange([type])}
                        className={cn(
                          "flex flex-1 flex-col items-center justify-center gap-2 rounded-xl border-2 border-solid p-3 transition-colors",
                          {
                            "border-brand-500 text-txt-brand": isActive,
                            "border-bordercolor-primary bg-transparent": !isActive,
                          },
                        )}
                      >
                        <IonIcon
                          icon={
                            type === "CAR"
                              ? carIcon
                              : type === "PUBLIC_TRANSPORT"
                                ? trainIcon
                                : dotsHorizontalIcon
                          }
                          className={`size-9 transition-colors ${
                            isActive ? "text-brand-primary" : "text-txt-secondary"
                          }`}
                        />
                        <span
                          className={`text-sm font-medium transition-colors ${
                            isActive ? "text-brand-primary" : "text-txt-secondary"
                          }`}
                        >
                          {type === "CAR"
                            ? t("fields:travelExpenses.type.options.car")
                            : type === "PUBLIC_TRANSPORT"
                              ? t("fields:travelExpenses.type.options.public-transport")
                              : t("fields:travelExpenses.type.options.other")}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Error message */}
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm font-medium text-error-120">{field.state.meta.errors[0]}</p>
                )}
              </div>
            )}
          />

          {/* File input */}
          <formTravelExpenses.AppField
            name="files"
            validators={{
              onChange: z
                .array(z.instanceof(File))
                .refine(
                  (files) =>
                    files.length === 0 || files.every((file) => file.size <= 10 * 1024 * 1024),
                  {
                    message: t("actions:declare-travel-expenses.file-too-large", { size: "10MB" }),
                  },
                )
                .refine(
                  (files) =>
                    files.length === 0 ||
                    files.every((file) =>
                      [
                        "image/jpeg",
                        "image/png",
                        "image/jpg",
                        "image/heic",
                        "image/heif",
                        "application/pdf",
                      ].includes(file.type),
                    ),
                  { message: t("actions:declare-travel-expenses.file-type") },
                )
                .refine((files) => files.length <= 5, {
                  message: "Maximaal 5 bijlagen toegestaan",
                }),
              onBlur: z
                .array(z.instanceof(File))
                .refine(
                  (files) =>
                    files.length === 0 || files.every((file) => file.size <= 10 * 1024 * 1024),
                  {
                    message: t("actions:declare-travel-expenses.file-too-large", { size: "10MB" }),
                  },
                )
                .refine(
                  (files) =>
                    files.length === 0 ||
                    files.every((file) =>
                      [
                        "image/jpeg",
                        "image/png",
                        "image/jpg",
                        "image/heic",
                        "image/heif",
                        "application/pdf",
                      ].includes(file.type),
                    ),
                  { message: t("actions:declare-travel-expenses.file-type") },
                )
                .refine((files) => files.length <= 5, {
                  message: "Maximaal 5 bijlagen toegestaan",
                }),
              onSubmit: z
                .array(z.instanceof(File))
                .min(1, t("actions:declare-travel-expenses.attachment-required"))
                .refine((files) => files.every((file) => file.size <= 10 * 1024 * 1024), {
                  message: t("actions:declare-travel-expenses.file-too-large", { size: "10MB" }),
                })
                .refine(
                  (files) =>
                    files.every((file) =>
                      [
                        "image/jpeg",
                        "image/png",
                        "image/jpg",
                        "image/heic",
                        "image/heif",
                        "application/pdf",
                      ].includes(file.type),
                    ),
                  { message: t("actions:declare-travel-expenses.file-type") },
                )
                .refine((files) => files.length <= 5, {
                  message: "Maximaal 5 bijlagen toegestaan",
                }),
            }}
            children={(field: any) => {
              const files: File[] = Array.isArray(field.state.value) ? field.state.value : [];
              const hasFile = files.length > 0;
              const hasError = field.state.meta.errors.length > 0;
              const atMax = files.length >= 5;
              const descriptions: string[] = formTravelExpenses.state.values.fileDescriptions ?? [];

              const removeAt = (index: number) => {
                field.handleChange(files.filter((_, i) => i !== index));
                formTravelExpenses.setFieldValue(
                  "fileDescriptions",
                  descriptions.filter((_, i) => i !== index),
                );
              };
              const setDescriptionAt = (index: number, value: string) => {
                const next = [...descriptions];
                next[index] = value;
                formTravelExpenses.setFieldValue("fileDescriptions", next);
              };

              return (
                <div className="flex flex-col gap-2">
                  <label className="text-base font-medium text-txt-primary">
                    {t("fields:travelExpenses.files.label")}
                  </label>

                  {/* Hidden file input - ref-based approach prevents memory leaks */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/jpg,image/heic,image/heif,.heic,.heif,application/pdf"
                    className="hidden"
                    onChange={(event) => {
                      const selectedFiles = Array.from(event.target.files || []);
                      // Merge with already-selected files; hard cap at 5.
                      field.handleChange([...files, ...selectedFiles].slice(0, 5));
                      // Reset so the same file can be re-picked after removal.
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                      }
                    }}
                  />

                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={atMax}
                    className={cn(
                      "flex w-full items-center justify-between gap-3 rounded-lg border border-solid px-4 py-3 transition-colors disabled:opacity-60",
                      {
                        "border-bordercolor-primary bg-transparent": !hasError,
                        "bg-error-100/5 border-error-100": hasError,
                      },
                    )}
                  >
                    <span
                      className={cn("text-base font-medium", {
                        "text-txt-tertiary": !hasError,
                        "text-error-120": hasError,
                      })}
                    >
                      {atMax
                        ? "Maximaal 5 bijlagen toegevoegd"
                        : t("fields:travelExpenses.files.placeholder")}
                    </span>
                    <IonIcon
                      icon={fileIcon}
                      className={cn("size-5 transition-colors", {
                        "text-txt-tertiary": !hasError,
                        "text-error-120": hasError,
                      })}
                    />
                  </button>

                  {/* Selected files: each removable, with an optional description */}
                  {hasFile && (
                    <ul className="flex flex-col gap-2">
                      {files.map((file, index) => (
                        <li
                          key={`${file.name}-${index}`}
                          className="flex flex-col gap-2 rounded-lg border border-solid border-bordercolor-primary p-3"
                        >
                          <div className="flex items-center gap-2">
                            <span className="flex-1 truncate text-base font-medium text-txt-primary">
                              {file.name}
                            </span>
                            <button
                              type="button"
                              aria-label="Bijlage verwijderen"
                              onClick={() => removeAt(index)}
                              className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-solid border-bordercolor-primary bg-transparent transition-colors active:bg-surface-secondary"
                            >
                              {/* Standalone glyph in a remove button, no text beside it: 3:1 is
                                  the norm and `error-100` clears it on this surface. */}
                              {/* eslint-disable-next-line no-restricted-syntax */}
                              <IonIcon icon={crossIcon} className="size-5 text-error-100" />
                            </button>
                          </div>
                          <input
                            type="text"
                            defaultValue={descriptions[index] ?? ""}
                            onChange={(event) => setDescriptionAt(index, event.target.value)}
                            placeholder="Omschrijving (optioneel)"
                            maxLength={200}
                            className="w-full rounded-lg border border-solid border-bordercolor-primary bg-transparent px-3 py-2 text-sm text-txt-primary placeholder:text-txt-tertiary"
                          />
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Error message */}
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm font-medium text-error-120">
                      {field.state.meta.errors[0]}
                    </p>
                  )}

                  {/* Helper text */}
                  <p className="text-xs text-txt-tertiary">
                    {t("fields:travelExpenses.files.helperText")}
                  </p>
                </div>
              );
            }}
          />

          {/* Unit toggle + amount input.
              A declaration is EITHER a euro amount OR a kilometer distance (XOR). Only the input
              for the selected unit is mounted, so only it is validated/submitted. On toggle we
              `deleteField` the now-inactive amount field to drop its value + validation state so a
              stale/empty euro value can never block a kilometer submit (and vice versa). */}
          <formTravelExpenses.AppField
            name="unit"
            children={(unitField: any) => {
              const unit: "EUROS" | "KILOMETERS" = unitField.state.value ?? "EUROS";
              const selectUnit = (next: "EUROS" | "KILOMETERS") => {
                if (next === unit) {
                  return;
                }
                unitField.handleChange(next);
                // Remove the field for the unit we just left so its value/meta can't block submit.
                formTravelExpenses.deleteField?.(next === "KILOMETERS" ? "cost" : "kilometers");
              };

              return (
                <div className="flex flex-col gap-4">
                  {/* Toggle */}
                  <div className="flex flex-col gap-2">
                    <label className="text-base font-medium text-txt-primary">
                      {t("fields:travelExpenses.unit.label")}
                    </label>
                    <div className="flex gap-3 *:flex-1">
                      {(["EUROS", "KILOMETERS"] as const).map((option) => {
                        const isActive = unit === option;
                        return (
                          <button
                            key={option}
                            type="button"
                            aria-pressed={isActive}
                            onClick={() => selectUnit(option)}
                            className={cn(
                              "flex flex-1 items-center justify-center gap-2 rounded-xl border-2 border-solid p-3 transition-colors",
                              {
                                "border-brand-500 text-txt-brand": isActive,
                                "border-bordercolor-primary bg-transparent text-txt-secondary":
                                  !isActive,
                              },
                            )}
                          >
                            <span className="text-sm font-medium">
                              {option === "EUROS"
                                ? t("fields:travelExpenses.unit.options.euros")
                                : t("fields:travelExpenses.unit.options.kilometers")}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Euro amount input (EUROS mode) */}
                  {unit === "EUROS" && (
                    <formTravelExpenses.AppField
                      name="cost"
                      validators={{
                        onChange: z.coerce
                          .number()
                          .min(0.01, t("fields:travelExpenses.cost.error.min"))
                          .max(
                            200,
                            t("actions:declare-travel-expenses.amount-max", { max: "€200" }),
                          ),
                        onBlur: z.coerce
                          .number()
                          .min(0.01, t("fields:travelExpenses.cost.error.min"))
                          .max(
                            200,
                            t("actions:declare-travel-expenses.amount-max", { max: "€200" }),
                          ),
                        onSubmit: z.coerce
                          .number()
                          .min(0.01, t("fields:travelExpenses.cost.error.min"))
                          .max(
                            200,
                            t("actions:declare-travel-expenses.amount-max", { max: "€200" }),
                          ),
                      }}
                      children={(field: any) => (
                        <field.InputField
                          value={
                            typeof field.state.value === "number"
                              ? field.state.value.toFixed(2)
                              : field.state.value
                          }
                          onIonChange={(event: any) =>
                            field.handleChange(Number(event.detail.value))
                          }
                          type="number"
                          min="0"
                          step="0.01"
                          name="travelExpenses.cost"
                          withPlaceholder
                          children={
                            <span
                              slot="start"
                              aria-hidden="true"
                              className="pointer-events-none my-auto !-me-1 !ms-3 select-none text-lg font-medium"
                            >
                              €
                            </span>
                          }
                        />
                      )}
                    />
                  )}

                  {/* Kilometers input (KILOMETERS mode). The server computes euros from the
                      km-rate, so we only collect the distance here. */}
                  {unit === "KILOMETERS" && (
                    <formTravelExpenses.AppField
                      name="kilometers"
                      validators={{
                        onChange: z.coerce
                          .number()
                          .min(0.01, t("fields:travelExpenses.kilometers.error.min")),
                        onBlur: z.coerce
                          .number()
                          .min(0.01, t("fields:travelExpenses.kilometers.error.min")),
                        onSubmit: z.coerce
                          .number()
                          .min(0.01, t("fields:travelExpenses.kilometers.error.min")),
                      }}
                      children={(field: any) => (
                        <field.InputField
                          value={
                            typeof field.state.value === "number"
                              ? String(field.state.value)
                              : field.state.value
                          }
                          onIonChange={(event: any) =>
                            field.handleChange(Number(event.detail.value))
                          }
                          type="number"
                          min="0"
                          step="0.1"
                          name="travelExpenses.kilometers"
                          withHelperText
                          children={
                            <span
                              slot="end"
                              aria-hidden="true"
                              className="pointer-events-none my-auto !-ms-1 !me-3 select-none text-lg font-medium text-txt-tertiary"
                            >
                              km
                            </span>
                          }
                        />
                      )}
                    />
                  )}
                </div>
              );
            }}
          />

          {/* Note input */}
          <formTravelExpenses.AppField
            name="note"
            validators={{ onChange: z.string(), onSubmit: z.string() }}
            children={(field: any) => (
              <field.InputField name="travelExpenses.note" withHelperText />
            )}
          />

          {/* Submit button */}
          <formTravelExpenses.Subscribe
            selector={(state: any) => [state.canSubmit, state.isSubmitting, state.values] as const}
            children={([canSubmit, isSubmitting, values]: [boolean, boolean, any]) => {
              // DEFENSIVE: Additional client-side check to prevent submission with invalid data
              // This prevents crashes even if form validation state is inconsistent.
              // The required amount depends on the selected unit (euros XOR kilometers).
              const unit: "EUROS" | "KILOMETERS" = values.unit ?? "EUROS";
              const hasAmount =
                unit === "KILOMETERS"
                  ? values.kilometers !== null &&
                    values.kilometers !== undefined &&
                    values.kilometers > 0
                  : values.cost !== null && values.cost !== undefined && values.cost > 0;
              const hasRequiredFields =
                values.types?.length > 0 && values.files?.length > 0 && hasAmount;

              const isButtonDisabled = !canSubmit || isSubmitting || !hasRequiredFields;

              return (
                <button
                  type="submit"
                  disabled={isButtonDisabled}
                  className="w-full rounded-xl bg-button-primary py-3 text-center text-base font-semibold text-txt-inverted disabled:bg-surface-tertiary disabled:text-txt-tertiary"
                >
                  {isSubmitting ? t("common:loading") : t("actions:declare-travel-expenses.save")}
                </button>
              );
            }}
          />
        </div>
      </FormElementExpenses>
    </ModalSheetAutoHeight>
  );
}
